import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import api, { formatErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useSite } from "@/context/SiteContext";
import { StreakMeter, CheckRow, LogCheckModal, PendingSignOffPanel, RectificationPanel } from "@/components/HygieneShared";
import FacilityProgressDrawer from "@/components/reports/FacilityProgressDrawer";
import ClosedTodayBanner from "@/components/ClosedTodayBanner";
import {
  Sparkles, Plus, Building2, AlertTriangle,
  Clock, CheckCircle2, ChevronDown, ChevronRight, ArrowLeft, Repeat,
} from "lucide-react";

const TEMPLATE_CODE = "CLEAN_FACILITY";
const STREAK_DAYS = 14;

const SUB_AREA_LABEL = { production: "Production", restroom: "Restroom", storage: "Storage" };

// Helper — clone the template keeping only the sub-areas this facility tracks.
function trimTemplate(template, allowedSubAreas) {
  if (!template) return template;
  const allow = new Set(allowedSubAreas || []);
  const sections = (template.sections || []).filter(
    (s) => !s.sub_area || allow.has(s.sub_area),
  );
  return { ...template, sections };
}

export default function DailyCleanliness({ embedded = false }) {
  const { user: me } = useAuth();
  const { activeSiteId, isAllSites, visibleSites, loaded: sitesLoaded, setActiveSite } = useSite();
  const isManagerPlus = ["owner", "manager", "supervisor"].includes(me?.role);
  const [searchParams, setSearchParams] = useSearchParams();

  const [template, setTemplate] = useState(null);
  const [summary, setSummary] = useState({ sites: [], facilities: [] });
  const [pending, setPending] = useState([]);
  const [rectQueue, setRectQueue] = useState([]);
  const [apprQueue, setApprQueue] = useState([]);
  // Sign-off backlog scope: "" → rolling 30 days (default), or a
  // YYYY-MM string → precise month window. Manager+ only.
  const [pendingMonth, setPendingMonth] = useState("");
  const [monthHasData, setMonthHasData] = useState(true);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [logFor, setLogFor] = useState(null);     // facility object or null
  const [drillFor, setDrillFor] = useState(null); // facility id for detail view

  const load = async () => {
    setLoading(true); setErr("");
    try {
      const baseParams = new URLSearchParams({ template_code: TEMPLATE_CODE, days: String(STREAK_DAYS) });
      if (!isAllSites && activeSiteId) baseParams.set("site_id", activeSiteId);

      const rectParams = new URLSearchParams({ template_code: TEMPLATE_CODE, days: "60", awaiting_rectification: "true" });
      if (!isAllSites && activeSiteId) rectParams.set("site_id", activeSiteId);

      const calls = [
        api.get("/checks/templates"),
        api.get(`/checks/facility-summary?${baseParams}`),
        api.get(`/checks?${rectParams}`),
      ];
      if (isManagerPlus) {
        const pendParams = new URLSearchParams({ pending_verification: "true", template_code: TEMPLATE_CODE });
        if (pendingMonth) pendParams.set("month", pendingMonth);
        else pendParams.set("days", "30");
        if (!isAllSites && activeSiteId) pendParams.set("site_id", activeSiteId);
        const apprParams = new URLSearchParams({ template_code: TEMPLATE_CODE, days: "60", awaiting_approval: "true" });
        if (!isAllSites && activeSiteId) apprParams.set("site_id", activeSiteId);
        calls.push(api.get(`/checks?${pendParams}`));
        calls.push(api.get(`/checks?${apprParams}`));
      }
      const res = await Promise.all(calls);
      const tpl = (res[0].data.templates || []).find((t) => t.code === TEMPLATE_CODE);
      setTemplate(tpl || null);
      setSummary(res[1].data);
      setRectQueue(res[2].data.checks || []);
      setPending(isManagerPlus ? (res[3].data.checks || []) : []);
      setApprQueue(isManagerPlus ? (res[4].data.checks || []) : []);
      // Does the selected backlog month hold ANY checks? Lets the panel tell
      // "all signed off" apart from "no data logged for this month".
      if (isManagerPlus && pendingMonth) {
        const mp = new URLSearchParams({ template_code: TEMPLATE_CODE, month: pendingMonth });
        if (!isAllSites && activeSiteId) mp.set("site_id", activeSiteId);
        const mr = await api.get(`/checks?${mp}`);
        setMonthHasData((mr.data.checks || []).length > 0);
      } else {
        setMonthHasData(true);
      }
    } catch (e) {
      setErr(formatErr(e.response?.data?.detail) || "Failed to load cleanliness data");
    } finally { setLoading(false); }
  };

  useEffect(() => {
    if (!sitesLoaded) return;
    load();
    // eslint-disable-next-line
  }, [sitesLoaded, activeSiteId, isAllSites, me?.id, pendingMonth]);

  // Deep-link from a Cleanliness QR (if/when added): just switch active site.
  const qpSite = searchParams.get("site_id");
  useEffect(() => {
    if (!sitesLoaded || !qpSite) return;
    if (activeSiteId !== qpSite) {
      const found = visibleSites.find((s) => s.id === qpSite);
      if (found) setActiveSite(qpSite);
    }
    if (qpSite) {
      const next = new URLSearchParams(searchParams);
      next.delete("site_id"); next.delete("from");
      setSearchParams(next, { replace: true });
    }
    // eslint-disable-next-line
  }, [sitesLoaded, qpSite]);

  // Deep-link from per-facility Cleanliness QR scan: auto-open Log modal.
  const qpFacility = searchParams.get("facility");
  const qpAction = searchParams.get("action");
  useEffect(() => {
    if (!qpFacility || qpAction !== "log") return;
    const facs = summary.facilities || [];
    if (!facs.length) return;
    const fac = facs.find((f) => f.id === qpFacility);
    if (fac) {
      setLogFor(fac);
      if (fac.site_id && fac.site_id !== activeSiteId) setActiveSite(fac.site_id);
    }
    const next = new URLSearchParams(searchParams);
    next.delete("facility"); next.delete("action"); next.delete("from");
    setSearchParams(next, { replace: true });
    // eslint-disable-next-line
  }, [qpFacility, qpAction, summary.facilities]);

  if (loading) return <div className="vb-card p-6 opacity-70" data-testid="cleanliness-loading">Loading…</div>;

  const facilities = summary.facilities || [];
  const totals = {
    facilities: facilities.length,
    logged: facilities.filter((f) => f.today_logged).length,
    fail: facilities.filter((f) => f.today_status === "fail").length,
    pending: facilities.reduce((s, f) => s + (f.pending_count || 0), 0),
  };

  // Drill-down: a single facility's full record list.
  if (drillFor) {
    const fac = facilities.find((f) => f.id === drillFor);
    return (
      <FacilityDetail
        facility={fac} template={template}
        canVerify={isManagerPlus}
        onBack={() => { setDrillFor(null); load(); }}
        onLog={() => setLogFor(fac)}
      />
    );
  }

  return (
    <div className="space-y-5 vb-fade-in" data-testid="daily-cleanliness-page">
      {!embedded && (
        <header>
          <h1 className="vb-heading text-3xl m-0 mb-1" style={{ color: "var(--vb-text-2)" }}>
            <Sparkles size={24} className="inline-block mr-2 -mt-1" />
            Daily Cleanliness
          </h1>
          <p className="text-sm opacity-70 m-0">
            Per-facility VB-PRP-07 audit. Each facility tracks its own selected areas (Production / Restroom / Storage).
          </p>
        </header>
      )}

      {err && <div className="vb-pill err" data-testid="cleanliness-error">{err}</div>}

      {!isAllSites && <ClosedTodayBanner siteId={activeSiteId} />}

      {/* Compliance ribbon */}
      <div className="vb-card p-4 flex flex-wrap items-center gap-4" data-testid="cleanliness-ribbon">
        <RibbonStat label="Today logged" value={`${totals.logged} / ${totals.facilities}`} accent={totals.logged === totals.facilities && totals.facilities > 0} testid="ribbon-logged" />
        <RibbonStat label="With failures" value={totals.fail} warn={totals.fail > 0} testid="ribbon-fail" />
        {isManagerPlus && (
          <RibbonStat label="Pending action" value={totals.pending} warn={totals.pending > 0} testid="ribbon-pending" />
        )}
        <span className="text-xs opacity-60 ml-auto">
          {isAllSites ? `${visibleSites.length} site${visibleSites.length === 1 ? "" : "s"}` : "1 site"} · last {STREAK_DAYS} days streak window
        </span>
      </div>

      {/* Filing readiness — see DailyHygiene for rationale. Hidden in
          All-Sites mode. */}
      <FacilityProgressDrawer templateCode={TEMPLATE_CODE} />

      {/* Rectification queue — staff side, visible to everyone with site access. Pinned above the log checks. */}
      <RectificationPanel
        records={rectQueue} template={template} onChanged={load}
        title="Awaiting rectification by staff" mode="rectify"
      />

      {/* Sites + facilities list */}
      {facilities.length === 0 ? (
        <div className="vb-card p-8 text-center opacity-70" data-testid="cleanliness-empty">
          <Building2 size={36} className="mx-auto opacity-40 mb-3" />
          <p className="text-sm mb-1">No facilities with Daily Cleanliness enabled.</p>
          <p className="text-xs opacity-70">Enable the <strong>Daily Cleanliness</strong> module per facility under <strong>Sites → Edit facility</strong>.</p>
        </div>
      ) : isAllSites ? (
        <AllSitesView
          summary={summary}
          onLog={(f) => setLogFor(f)} onDrill={(id) => setDrillFor(id)}
        />
      ) : (
        <SingleSiteView
          facilities={facilities}
          onLog={(f) => setLogFor(f)} onDrill={(id) => setDrillFor(id)}
        />
      )}

      {/* Approval queue — supervisor side */}
      {isManagerPlus && (
        <RectificationPanel
          records={apprQueue} template={template} onChanged={load}
          title="Awaiting your approval" mode="approve"
        />
      )}

      {/* Pending action panel */}
      {isManagerPlus && (
        <PendingSignOffPanel
          pending={pending} template={template}
          onChanged={load}
          month={pendingMonth} onMonthChange={setPendingMonth}
          monthHasData={monthHasData}
        />
      )}

      {logFor && template && (
        <LogCheckModal
          template={trimTemplate(template, logFor.cleanliness_areas)}
          targetId={logFor.id}
          facility={logFor}
          onClose={() => setLogFor(null)}
          onSaved={() => { setLogFor(null); load(); }}
        />
      )}
    </div>
  );
}

// ---------------- Ribbon stat ----------------
function RibbonStat({ label, value, accent, warn, testid }) {
  return (
    <div data-testid={testid}>
      <div className="text-[10px] uppercase tracking-wider opacity-60">{label}</div>
      <div
        className="vb-heading text-xl leading-none"
        style={{ color: warn ? "var(--vb-error)" : accent ? "var(--vb-accent)" : "var(--vb-text-2)" }}
      >
        {value}
      </div>
    </div>
  );
}

// ---------------- Today status pill ----------------
function StatusPill({ status, testid }) {
  if (status === "pass")
    return <span className="vb-pill ok text-[10px]" data-testid={testid}><CheckCircle2 size={10} /> Logged · all pass</span>;
  if (status === "fail")
    return <span className="vb-pill err text-[10px]" data-testid={testid}><AlertTriangle size={10} /> Logged · with fails</span>;
  return <span className="vb-pill warn text-[10px]" data-testid={testid}><Clock size={10} /> Not yet today</span>;
}

// ---------------- Areas badge ----------------
function AreasBadge({ areas }) {
  const list = (areas || []).map((a) => SUB_AREA_LABEL[a] || a).join(" · ");
  return <span className="text-[10px] opacity-60">{list || "—"}</span>;
}

// ---------------- Facility row ----------------
function FacilityRow({ f, onLog, onDrill }) {
  return (
    <div className="flex items-center gap-3 py-3 flex-wrap" data-testid={`facility-row-${f.id}`}>
      <div className="flex-1 min-w-[160px]">
        <div className="text-sm font-medium">{f.name}</div>
        <div className="text-[11px] opacity-60">
          PIC: {f.person_in_charge || "—"} · Areas: <AreasBadge areas={f.cleanliness_areas} />
        </div>
      </div>
      <StatusPill status={f.today_status} testid={`facility-status-${f.id}`} />
      {(f.today_record_count || 0) > 1 && (
        <span className="vb-pill text-[10px]" title="Multiple checks logged today" data-testid={`facility-multi-${f.id}`}>
          <Repeat size={10} /> {f.today_record_count} today
        </span>
      )}
      <div className="text-right" style={{ minWidth: 90 }}>
        <div className="text-[10px] uppercase tracking-wider opacity-60">14d streak</div>
        <div className="text-sm font-medium" style={{ color: f.current_streak > 0 ? "var(--vb-accent)" : undefined }}>
          {f.current_streak}d · {f.compliance_pct}%
        </div>
      </div>
      {f.pending_count > 0 && (
        <span className="vb-pill warn text-[10px]" data-testid={`facility-pending-${f.id}`}>
          {f.pending_count} pending
        </span>
      )}
      <button className="vb-btn text-sm" onClick={() => onLog(f)} data-testid={`facility-log-${f.id}`}>
        <Plus size={13} /> Log check
      </button>
      <button className="vb-btn ghost text-sm" onClick={() => onDrill(f.id)} data-testid={`facility-drill-${f.id}`}>
        Records
      </button>
    </div>
  );
}

// ---------------- All-sites view ----------------
function AllSitesView({ summary, onLog, onDrill }) {
  const [open, setOpen] = useState(() => new Set((summary.sites || []).map((s) => s.id)));
  const toggle = (sid) => {
    const next = new Set(open);
    if (next.has(sid)) next.delete(sid); else next.add(sid);
    setOpen(next);
  };
  return (
    <div className="space-y-3" data-testid="all-sites-view">
      {(summary.sites || []).map((s) => {
        const isOpen = open.has(s.id);
        const facs = summary.facilities.filter((f) => f.site_id === s.id);
        return (
          <div key={s.id} className="vb-card p-0 overflow-hidden" data-testid={`site-block-${s.id}`}>
            <button
              className="w-full flex items-center gap-3 p-4 text-left"
              onClick={() => toggle(s.id)}
              data-testid={`site-toggle-${s.id}`}
              style={{ background: "transparent", border: "none", cursor: "pointer" }}
            >
              {isOpen ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
              <Building2 size={16} style={{ color: "var(--vb-text-2)" }} />
              <strong className="text-sm">{s.name}</strong>
              <span className="text-xs opacity-60">· {s.facility_count} facilit{s.facility_count === 1 ? "y" : "ies"}</span>
              <span className="ml-auto text-xs flex gap-3">
                <span style={{ color: s.today_logged === s.facility_count && s.facility_count > 0 ? "var(--vb-accent)" : undefined }}>
                  {s.today_logged}/{s.facility_count} today
                </span>
                {s.today_fail > 0 && <span style={{ color: "var(--vb-error)" }}>{s.today_fail} fail</span>}
                {s.pending > 0 && <span className="vb-pill warn text-[10px]">{s.pending} pending</span>}
              </span>
            </button>
            {isOpen && (
              <div className="border-t" style={{ borderColor: "rgba(0,0,0,0.06)" }}>
                <div className="divide-y px-4" style={{ borderColor: "rgba(0,0,0,0.06)" }}>
                  {facs.length === 0 ? (
                    <div className="py-4 text-xs opacity-60">No facilities under this site have Daily Cleanliness enabled.</div>
                  ) : (
                    facs.map((f) => <FacilityRow key={f.id} f={f} onLog={onLog} onDrill={onDrill} />)
                  )}
                </div>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

// ---------------- Single-site view ----------------
function SingleSiteView({ facilities, onLog, onDrill }) {
  return (
    <div className="vb-card p-0" data-testid="single-site-view">
      <div className="divide-y px-4" style={{ borderColor: "rgba(0,0,0,0.06)" }}>
        {facilities.map((f) => <FacilityRow key={f.id} f={f} onLog={onLog} onDrill={onDrill} />)}
      </div>
    </div>
  );
}

// ---------------- Facility detail / drill-down ----------------
function FacilityDetail({ facility, template, canVerify, onBack, onLog }) {
  const [streak, setStreak] = useState(null);
  const [records, setRecords] = useState([]);
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(true);
  const trimmed = useMemo(() => trimTemplate(template, facility?.cleanliness_areas), [template, facility]);

  const load = async () => {
    setLoading(true); setErr("");
    try {
      const [streakRes, recordsRes] = await Promise.all([
        api.get(`/checks/streak/facility/${facility.id}?template_code=${TEMPLATE_CODE}&days=${STREAK_DAYS}`),
        api.get(`/checks?scope_target_id=${facility.id}&template_code=${TEMPLATE_CODE}&days=30`),
      ]);
      setStreak(streakRes.data);
      setRecords(recordsRes.data.checks || []);
    } catch (e) {
      setErr(formatErr(e.response?.data?.detail) || "Failed to load");
    } finally { setLoading(false); }
  };

  useEffect(() => { if (facility?.id) load(); /* eslint-disable-next-line */ }, [facility?.id]);

  if (!facility) return <div className="vb-card p-6 opacity-70">Facility not found.</div>;
  if (loading) return <div className="vb-card p-6 opacity-70">Loading…</div>;

  return (
    <div className="space-y-4 vb-fade-in" data-testid="facility-detail">
      <button className="vb-btn ghost text-sm" onClick={onBack} data-testid="facility-detail-back">
        <ArrowLeft size={14} /> Back
      </button>
      <header className="flex items-start gap-3 flex-wrap">
        <div className="flex-1 min-w-[200px]">
          <h1 className="vb-heading text-2xl m-0">{facility.name}</h1>
          <p className="text-xs opacity-60 m-0">
            {facility.site_name} · PIC: {facility.person_in_charge || "—"} · Areas: <AreasBadge areas={facility.cleanliness_areas} />
          </p>
        </div>
      </header>
      {err && <div className="vb-pill err">{err}</div>}
      {streak && <StreakMeter streak={streak} label="Cleanliness streak" subtitle={`Last ${streak.days} days · ${trimmed?.title || "Daily Cleanliness"}`} />}
      <div className="vb-card p-5">
        <h3 className="vb-heading text-base m-0 mb-3">
          Recent records
          <span className="text-xs opacity-60 ml-2">last 30 days · {records.length}</span>
        </h3>
        {records.length === 0 ? (
          <div className="text-center py-6 opacity-60 text-sm" data-testid="detail-empty">
            No records yet for the last 30 days.
          </div>
        ) : (
          <div className="space-y-2" data-testid="detail-records">
            {records.map((c) => (
              <CheckRow
                key={c.id} check={c} template={trimmed}
                canVerify={canVerify && !c.verified_at}
                onVerified={load}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
